import React from 'react';
import styled from 'styled-components';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormGroup,
  Checkbox,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Tab,
  Tabs,
  TextField,
  Typography,
} from '@material-ui/core';
import { format, parseISO } from 'date-fns';

// ─── Styled ────────────────────────────────────────────────────────────────

const PageWrap = styled(Box)`
  padding: 24px;
  max-width: 1200px;
  margin: 0 auto;
`;

const Header = styled(Box)`
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 24px;
`;

const DateNav = styled(Box)`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatsRow = styled(Box)`
  display: flex;
  gap: 16px;
  margin-bottom: 24px;
`;

const StatCard = styled(Paper)`
  padding: 16px 24px;
  flex: 1;
  text-align: center;
`;

const ReservationRow = styled(Paper)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  gap: 16px;
  cursor: pointer;
  &:hover {
    background: #f5f5f5;
  }
`;

const TimeSlotGrid = styled(Box)`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(100px, 1fr));
  gap: 8px;
  margin-top: 8px;
`;

const TimeSlot = styled(Box)`
  padding: 8px;
  text-align: center;
  border-radius: 4px;
  cursor: ${p => p.available ? 'pointer' : 'default'};
  border: 2px solid ${p => p.selected ? '#0033FF' : p.available ? '#ddd' : '#f0f0f0'};
  background: ${p => p.selected ? '#0033FF' : p.available ? '#fff' : '#f9f9f9'};
  color: ${p => p.selected ? '#fff' : p.available ? '#111' : '#bbb'};
  font-size: 13px;
  font-weight: ${p => p.selected ? 600 : 400};
`;

const statusColors = {
  pending: '#ff9800',
  confirmed: '#2196f3',
  seated: '#4caf50',
  completed: '#9e9e9e',
  no_show: '#f44336',
  cancelled: '#bdbdbd',
};

const StatusChip = styled(Chip)`
  background-color: ${p => statusColors[p.status] || '#eee'} !important;
  color: #fff !important;
  font-size: 11px !important;
  height: 22px !important;
`;

const WaitlistRow = styled(Paper)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  gap: 16px;
`;

const GuestRow = styled(Paper)`
  display: flex;
  align-items: center;
  padding: 12px 16px;
  margin-bottom: 8px;
  gap: 16px;
`;

// ─── Helpers ────────────────────────────────────────────────────────────────

const fmtDate = (iso) => {
  try { return format(parseISO(iso + 'T12:00:00'), 'EEE, MMM d'); }
  catch { return iso; }
};

const fmtTime = (t) => {
  if (!t) return '';
  const [h, m] = t.split(':');
  const hour = parseInt(h, 10);
  return `${hour > 12 ? hour - 12 : hour || 12}:${m} ${hour >= 12 ? 'PM' : 'AM'}`;
};

const guestName = (guest) => guest ? `${guest.first_name} ${guest.last_name}` : 'Unknown';

// ─── Component ─────────────────────────────────────────────────────────────

const DAYS = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

const TYPE_LABELS = {
  reservation: { label: 'Reservable', color: '#2196f3' },
  walk_in: { label: 'Walk-in', color: '#ff9800' },
  hybrid: { label: 'Hybrid', color: '#9c27b0' },
};

const ReservationsView = ({
  selectedDate,
  activeTab,
  venueSections,
  servicePeriods,
  guests,
  reservations,
  availability,
  waitlist,
  totalCovers,
  isLoading,
  isAvailabilityLoading,
  isSaving,

  bookingModalOpen,
  editingReservation,
  bookingForm,
  guestModalOpen,
  guestForm,
  waitlistModalOpen,
  waitlistForm,
  guestSearch,

  spModalOpen,
  editingSp,
  spForm,
  isSavingSp,

  onDateChange,
  onTabChange,
  onOpenBooking,
  onCloseBooking,
  onBookingFormChange,
  onPartySizeChange,
  onBookingDateChange,
  onSaveBooking,
  onCancelReservation,
  onStatusChange,

  onOpenGuestModal,
  onCloseGuestModal,
  onGuestFormChange,
  onSaveGuest,
  onGuestSearch,

  onOpenWaitlistModal,
  onCloseWaitlistModal,
  onWaitlistFormChange,
  onSaveWaitlist,
  onWaitlistStatusChange,

  onOpenSpModal,
  onCloseSpModal,
  onSpFormChange,
  onSaveServicePeriod,
  onDeleteServicePeriod,

  sectionModalOpen,
  editingSection,
  sectionForm,
  isSavingSection,
  onOpenSectionModal,
  onCloseSectionModal,
  onSectionFormChange,
  onSaveSection,
  onDeleteSection,
}) => {
  const confirmed = reservations.filter(r => r.status === 'confirmed' || r.status === 'pending').length;
  const seated = reservations.filter(r => r.status === 'seated').length;
  const waitlistCount = waitlist.filter(w => w.status === 'waiting').length;

  return (
    <PageWrap>
      {/* Header */}
      <Header>
        <Typography variant="h5" style={{ fontWeight: 700 }}>Reservations</Typography>
        <Box display="flex" gap={8}>
          <Button variant="outlined" onClick={onOpenGuestModal} style={{ marginRight: 8 }}>
            + Add Guest
          </Button>
          <Button variant="contained" color="primary" onClick={() => onOpenBooking()}>
            + New Booking
          </Button>
        </Box>
      </Header>

      {/* Date Navigation */}
      <DateNav mb={2}>
        <Button size="small" onClick={() => onDateChange(-1)} style={{ minWidth: 36 }}>{'<'}</Button>
        <Typography variant="h6" style={{ minWidth: 160, textAlign: 'center' }}>
          {fmtDate(selectedDate)}
        </Typography>
        <Button size="small" onClick={() => onDateChange(1)} style={{ minWidth: 36 }}>{'>'}</Button>
      </DateNav>

      {/* Stats */}
      <StatsRow>
        <StatCard elevation={1}>
          <Typography variant="h4" style={{ fontWeight: 700, color: '#0033FF' }}>{reservations.length}</Typography>
          <Typography variant="body2" color="textSecondary">Bookings</Typography>
        </StatCard>
        <StatCard elevation={1}>
          <Typography variant="h4" style={{ fontWeight: 700 }}>{totalCovers}</Typography>
          <Typography variant="body2" color="textSecondary">Covers</Typography>
        </StatCard>
        <StatCard elevation={1}>
          <Typography variant="h4" style={{ fontWeight: 700, color: '#4caf50' }}>{seated}</Typography>
          <Typography variant="body2" color="textSecondary">Seated</Typography>
        </StatCard>
        <StatCard elevation={1}>
          <Typography variant="h4" style={{ fontWeight: 700, color: '#ff9800' }}>{waitlistCount}</Typography>
          <Typography variant="body2" color="textSecondary">Waitlist</Typography>
        </StatCard>
      </StatsRow>

      {/* Tabs */}
      <Tabs value={activeTab} onChange={(_, v) => onTabChange(v)} style={{ marginBottom: 16 }} variant="scrollable" scrollButtons="auto">
        <Tab label="Reservations" value="reservations" />
        <Tab label={`Waitlist (${waitlistCount})`} value="waitlist" />
        <Tab label="Guest Book" value="guests" />
        <Tab label="Venue" value="venue" />
        <Tab label="Service Periods" value="settings" />
      </Tabs>

      {/* Reservations Tab */}
      {activeTab === 'reservations' && (
        <Box>
          {isLoading ? (
            <Box display="flex" justifyContent="center" p={4}><CircularProgress /></Box>
          ) : reservations.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography color="textSecondary">No reservations for {fmtDate(selectedDate)}</Typography>
              <Button variant="contained" color="primary" style={{ marginTop: 16 }} onClick={() => onOpenBooking()}>
                Add First Booking
              </Button>
            </Box>
          ) : (
            reservations.map(r => (
              <ReservationRow key={r.id} elevation={1} onClick={() => onOpenBooking(r)}>
                <Box style={{ minWidth: 64, textAlign: 'center' }}>
                  <Typography variant="h6" style={{ fontWeight: 700 }}>{fmtTime(r.reservation_time)}</Typography>
                </Box>
                <Box flex={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                    {guestName(r.guest)}
                    {r.guest?.vip && <Chip label="VIP" size="small" style={{ marginLeft: 8, background: '#FFD700', fontSize: 10, height: 18 }} />}
                  </Typography>
                  <Typography variant="body2" color="textSecondary">
                    Party of {r.party_size}
                    {r.service_period && ` · ${r.service_period.name}`}
                    {r.notes && ` · ${r.notes}`}
                  </Typography>
                </Box>
                <StatusChip
                  label={r.status.replace('_', ' ')}
                  status={r.status}
                  size="small"
                  onClick={e => e.stopPropagation()}
                />
                {r.status === 'confirmed' && (
                  <Button size="small" variant="outlined" style={{ color: '#4caf50', borderColor: '#4caf50', marginLeft: 8 }}
                    onClick={e => { e.stopPropagation(); onStatusChange(r.id, 'seated'); }}>
                    Seat
                  </Button>
                )}
                {r.status === 'seated' && (
                  <Button size="small" variant="outlined" style={{ color: '#9e9e9e', borderColor: '#9e9e9e', marginLeft: 8 }}
                    onClick={e => { e.stopPropagation(); onStatusChange(r.id, 'completed'); }}>
                    Complete
                  </Button>
                )}
              </ReservationRow>
            ))
          )}
        </Box>
      )}

      {/* Waitlist Tab */}
      {activeTab === 'waitlist' && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" color="primary" onClick={onOpenWaitlistModal}>
              + Add to Waitlist
            </Button>
          </Box>
          {waitlist.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography color="textSecondary">Waitlist is empty</Typography>
            </Box>
          ) : (
            waitlist.map(w => (
              <WaitlistRow key={w.id} elevation={1}>
                <Box flex={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{guestName(w.guest)}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    Party of {w.party_size}
                    {w.preferred_time && ` · Preferred: ${fmtTime(w.preferred_time)}`}
                    {w.quoted_wait_mins && ` · ~${w.quoted_wait_mins} min wait`}
                  </Typography>
                </Box>
                <StatusChip label={w.status} status={w.status === 'waiting' ? 'pending' : w.status === 'seated' ? 'seated' : 'completed'} size="small" />
                {w.status === 'waiting' && (
                  <>
                    <Button size="small" variant="outlined" style={{ marginLeft: 8 }}
                      onClick={() => onWaitlistStatusChange(w.id, 'notified')}>Notify</Button>
                    <Button size="small" variant="outlined" style={{ color: '#4caf50', borderColor: '#4caf50', marginLeft: 8 }}
                      onClick={() => onWaitlistStatusChange(w.id, 'seated')}>Seat</Button>
                  </>
                )}
                {w.status === 'notified' && (
                  <Button size="small" variant="outlined" style={{ color: '#4caf50', borderColor: '#4caf50', marginLeft: 8 }}
                    onClick={() => onWaitlistStatusChange(w.id, 'seated')}>Seat</Button>
                )}
              </WaitlistRow>
            ))
          )}
        </Box>
      )}

      {/* Guest Book Tab */}
      {activeTab === 'guests' && (
        <Box>
          <Box display="flex" gap={8} mb={2}>
            <TextField
              placeholder="Search guests..."
              value={guestSearch}
              onChange={e => onGuestSearch(e.target.value)}
              variant="outlined"
              size="small"
              style={{ flex: 1 }}
            />
            <Button variant="contained" color="primary" onClick={onOpenGuestModal} style={{ marginLeft: 8 }}>
              + New Guest
            </Button>
          </Box>
          {guests.map(g => (
            <GuestRow key={g.id} elevation={1}>
              <Box flex={1}>
                <Typography variant="subtitle1" style={{ fontWeight: 600 }}>
                  {g.first_name} {g.last_name}
                  {g.vip && <Chip label="VIP" size="small" style={{ marginLeft: 8, background: '#FFD700', fontSize: 10, height: 18 }} />}
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  {[g.email, g.phone].filter(Boolean).join(' · ')}
                  {g.visit_count > 0 && ` · ${g.visit_count} visit${g.visit_count !== 1 ? 's' : ''}`}
                </Typography>
                {g.notes && <Typography variant="caption" color="textSecondary">{g.notes}</Typography>}
              </Box>
              {g.tags?.map(tag => (
                <Chip key={tag} label={tag} size="small" style={{ marginLeft: 4 }} />
              ))}
            </GuestRow>
          ))}
        </Box>
      )}

      {/* Venue Tab */}
      {activeTab === 'venue' && (
        <Box>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Box>
              <Typography variant="h6" style={{ fontWeight: 700 }}>Venue Sections</Typography>
              <Typography variant="body2" color="textSecondary">
                Define your space — reservable areas, walk-in zones, and hybrid sections.
              </Typography>
            </Box>
            <Button variant="contained" color="primary" onClick={() => onOpenSectionModal()}>
              + Add Section
            </Button>
          </Box>

          {/* Capacity summary */}
          {venueSections.length > 0 && (
            <Box display="flex" gap={16} mb={2} flexWrap="wrap">
              {['reservation', 'walk_in', 'hybrid'].map(type => {
                const sections = venueSections.filter(s => s.type === type && s.is_active);
                if (!sections.length) return null;
                const total = sections.reduce((sum, s) => sum + s.total_capacity, 0);
                const reservable = sections.reduce((sum, s) => {
                  if (type === 'reservation') return sum + s.total_capacity;
                  if (type === 'walk_in') return sum;
                  return sum + (s.reservation_capacity || 0);
                }, 0);
                return (
                  <StatCard key={type} elevation={1} style={{ flex: '1 1 140px', minWidth: 140 }}>
                    <Typography variant="h5" style={{ fontWeight: 700, color: TYPE_LABELS[type].color }}>
                      {type === 'walk_in' ? total : reservable}
                    </Typography>
                    <Typography variant="caption" color="textSecondary">
                      {TYPE_LABELS[type].label} seats
                    </Typography>
                  </StatCard>
                );
              })}
            </Box>
          )}

          {venueSections.length === 0 ? (
            <Box textAlign="center" py={6}>
              <Typography color="textSecondary">No sections configured yet</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, maxWidth: 440, margin: '8px auto 16px' }}>
                Add sections to define your space. Set walk-in zones, reservable areas, or hybrid sections that do both.
              </Typography>
              <Button variant="contained" color="primary" onClick={() => onOpenSectionModal()}>
                Add First Section
              </Button>
            </Box>
          ) : (
            venueSections.map(s => (
              <Paper key={s.id} style={{ padding: 16, marginBottom: 8 }} elevation={1}>
                <Box display="flex" alignItems="center" justifyContent="space-between">
                  <Box flex={1}>
                    <Box display="flex" alignItems="center" gap={8} mb={0.5}>
                      <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{s.name}</Typography>
                      <Chip
                        label={TYPE_LABELS[s.type]?.label}
                        size="small"
                        style={{ background: TYPE_LABELS[s.type]?.color, color: '#fff', marginLeft: 8 }}
                      />
                      {!s.is_active && <Chip label="Inactive" size="small" style={{ background: '#bdbdbd', color: '#fff', marginLeft: 4 }} />}
                    </Box>
                    <Typography variant="body2" color="textSecondary">
                      {s.total_capacity} total seats
                      {s.type === 'hybrid' && s.reservation_capacity != null && ` · ${s.reservation_capacity} reservable`}
                      {s.type === 'reservation' && ` · all reservable`}
                      {s.type === 'walk_in' && ` · walk-in only`}
                    </Typography>
                    {s.description && (
                      <Typography variant="caption" color="textSecondary">{s.description}</Typography>
                    )}
                  </Box>
                  <Box display="flex" alignItems="center">
                    <Button size="small" onClick={() => onOpenSectionModal(s)}>Edit</Button>
                    <Button size="small" style={{ color: '#f44336', marginLeft: 4 }}
                      onClick={() => onDeleteSection(s.id)}>Delete</Button>
                  </Box>
                </Box>
              </Paper>
            ))
          )}
        </Box>
      )}

      {/* Service Periods Tab */}
      {activeTab === 'settings' && (
        <Box>
          <Box display="flex" justifyContent="flex-end" mb={2}>
            <Button variant="contained" color="primary" onClick={() => onOpenSpModal()}>
              + Add Service Period
            </Button>
          </Box>
          {servicePeriods.map(sp => (
            <Paper key={sp.id} style={{ padding: 16, marginBottom: 8 }} elevation={1}>
              <Box display="flex" alignItems="center" justifyContent="space-between">
                <Box flex={1}>
                  <Typography variant="subtitle1" style={{ fontWeight: 600 }}>{sp.name}</Typography>
                  <Typography variant="body2" color="textSecondary">
                    {fmtTime(sp.start_time)} – {fmtTime(sp.end_time)}
                    {' · '}{sp.max_covers} covers max
                    {' · '}{sp.turn_time_mins} min turns
                    {' · '}{sp.slot_interval_mins} min slots
                    {sp.pacing_per_slot ? ` · ${sp.pacing_per_slot} covers/slot` : ''}
                  </Typography>
                  <Typography variant="caption" color="textSecondary">
                    {DAYS.filter((_, i) => sp.days_of_week?.includes(i)).join(' · ')}
                  </Typography>
                </Box>
                <Box display="flex" alignItems="center" gap={8}>
                  <Chip label={sp.is_active ? 'Active' : 'Inactive'} size="small"
                    style={{ background: sp.is_active ? '#4caf50' : '#bdbdbd', color: '#fff' }} />
                  <Button size="small" onClick={() => onOpenSpModal(sp)} style={{ marginLeft: 8 }}>Edit</Button>
                  <Button size="small" style={{ color: '#f44336', marginLeft: 4 }}
                    onClick={() => onDeleteServicePeriod(sp.id)}>Delete</Button>
                </Box>
              </Box>
            </Paper>
          ))}
          {servicePeriods.length === 0 && (
            <Box textAlign="center" py={6}>
              <Typography color="textSecondary">No service periods configured</Typography>
              <Typography variant="body2" color="textSecondary" style={{ marginTop: 8, maxWidth: 400, margin: '8px auto 16px' }}>
                Service periods define when guests can book. Set your hours, max capacity, and how long each table turns over.
              </Typography>
              <Button variant="contained" color="primary" onClick={() => onOpenSpModal()}>
                Create First Service Period
              </Button>
            </Box>
          )}
        </Box>
      )}

      {/* Booking Modal */}
      <Dialog open={bookingModalOpen} onClose={onCloseBooking} maxWidth="sm" fullWidth>
        <DialogTitle>{editingReservation ? 'Edit Reservation' : 'New Booking'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel>Guest</InputLabel>
                <Select
                  value={bookingForm.guest_id}
                  onChange={e => onBookingFormChange('guest_id', e.target.value)}
                  label="Guest"
                >
                  {guests.map(g => (
                    <MenuItem key={g.id} value={g.id}>{g.first_name} {g.last_name}{g.phone ? ` · ${g.phone}` : ''}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Date"
                type="date"
                value={bookingForm.reservation_date}
                onChange={e => onBookingDateChange(e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Party Size"
                type="number"
                value={bookingForm.party_size}
                onChange={e => onPartySizeChange(Number(e.target.value))}
                variant="outlined"
                size="small"
                fullWidth
                inputProps={{ min: 1, max: 20 }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" gutterBottom style={{ fontWeight: 600 }}>Select Time</Typography>
              {isAvailabilityLoading ? (
                <CircularProgress size={20} />
              ) : (
                <TimeSlotGrid>
                  {availability.map(slot => (
                    <TimeSlot
                      key={slot.time}
                      available={slot.available ? 1 : 0}
                      selected={bookingForm.reservation_time === slot.time ? 1 : 0}
                      onClick={() => slot.available && onBookingFormChange('reservation_time', slot.time)}
                    >
                      {fmtTime(slot.time)}
                      {slot.available && (
                        <div style={{ fontSize: 10, opacity: 0.7 }}>{slot.remaining_covers} left</div>
                      )}
                    </TimeSlot>
                  ))}
                  {availability.length === 0 && (
                    <Typography variant="caption" color="textSecondary">
                      No availability configured — create a service period first.
                    </Typography>
                  )}
                </TimeSlotGrid>
              )}
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Status</InputLabel>
                <Select
                  value={bookingForm.status}
                  onChange={e => onBookingFormChange('status', e.target.value)}
                  label="Status"
                >
                  <MenuItem value="pending">Pending</MenuItem>
                  <MenuItem value="confirmed">Confirmed</MenuItem>
                  <MenuItem value="seated">Seated</MenuItem>
                  <MenuItem value="completed">Completed</MenuItem>
                  <MenuItem value="no_show">No Show</MenuItem>
                  <MenuItem value="cancelled">Cancelled</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Source</InputLabel>
                <Select
                  value={bookingForm.source}
                  onChange={e => onBookingFormChange('source', e.target.value)}
                  label="Source"
                >
                  <MenuItem value="direct">Direct</MenuItem>
                  <MenuItem value="phone">Phone</MenuItem>
                  <MenuItem value="walk_in">Walk-in</MenuItem>
                  <MenuItem value="widget">Widget</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField
                label="Notes"
                value={bookingForm.notes}
                onChange={e => onBookingFormChange('notes', e.target.value)}
                variant="outlined"
                size="small"
                fullWidth
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          {editingReservation && (
            <Button
              onClick={() => { onCancelReservation(editingReservation.id); onCloseBooking(); }}
              style={{ color: '#f44336', marginRight: 'auto' }}
            >
              Cancel Reservation
            </Button>
          )}
          <Button onClick={onCloseBooking}>Dismiss</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveBooking}
            disabled={!bookingForm.guest_id || !bookingForm.reservation_time || isSaving}
          >
            {isSaving ? <CircularProgress size={18} /> : editingReservation ? 'Update' : 'Book'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Guest Modal */}
      <Dialog open={guestModalOpen} onClose={onCloseGuestModal} maxWidth="sm" fullWidth>
        <DialogTitle>New Guest</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={6}>
              <TextField label="First Name" value={guestForm.first_name}
                onChange={e => onGuestFormChange('first_name', e.target.value)}
                variant="outlined" size="small" fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Last Name" value={guestForm.last_name}
                onChange={e => onGuestFormChange('last_name', e.target.value)}
                variant="outlined" size="small" fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Email" value={guestForm.email}
                onChange={e => onGuestFormChange('email', e.target.value)}
                variant="outlined" size="small" fullWidth />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Phone" value={guestForm.phone}
                onChange={e => onGuestFormChange('phone', e.target.value)}
                variant="outlined" size="small" fullWidth />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Notes" value={guestForm.notes}
                onChange={e => onGuestFormChange('notes', e.target.value)}
                variant="outlined" size="small" fullWidth multiline rows={2} />
            </Grid>
            <Grid item xs={12}>
              <Box display="flex" alignItems="center" gap={8}>
                <Typography>VIP</Typography>
                <Switch checked={guestForm.vip} onChange={e => onGuestFormChange('vip', e.target.checked)} color="primary" />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseGuestModal}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={onSaveGuest}
            disabled={!guestForm.first_name || !guestForm.last_name}>
            Save Guest
          </Button>
        </DialogActions>
      </Dialog>

      {/* Venue Section Modal */}
      <Dialog open={sectionModalOpen} onClose={onCloseSectionModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSection ? 'Edit Section' : 'New Venue Section'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                label="Section Name (e.g. Main Floor, Patio, Bar)"
                value={sectionForm.name}
                onChange={e => onSectionFormChange('name', e.target.value)}
                variant="outlined" size="small" fullWidth
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Type</InputLabel>
                <Select
                  value={sectionForm.type}
                  onChange={e => onSectionFormChange('type', e.target.value)}
                  label="Type"
                >
                  <MenuItem value="reservation">Reservable — guests book specific slots online</MenuItem>
                  <MenuItem value="walk_in">Walk-in only — first come first served, no online booking</MenuItem>
                  <MenuItem value="hybrid">Hybrid — some seats reservable, rest walk-in</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={sectionForm.type === 'hybrid' ? 6 : 12}>
              <TextField
                label="Total Seats"
                type="number"
                value={sectionForm.total_capacity}
                onChange={e => onSectionFormChange('total_capacity', Number(e.target.value))}
                variant="outlined" size="small" fullWidth
                inputProps={{ min: 1 }}
                helperText="Total physical seats in this section"
              />
            </Grid>
            {sectionForm.type === 'hybrid' && (
              <Grid item xs={6}>
                <TextField
                  label="Reservable Seats"
                  type="number"
                  value={sectionForm.reservation_capacity}
                  onChange={e => onSectionFormChange('reservation_capacity', e.target.value)}
                  variant="outlined" size="small" fullWidth
                  inputProps={{ min: 0, max: sectionForm.total_capacity }}
                  helperText="How many can be booked online"
                />
              </Grid>
            )}
            <Grid item xs={12}>
              <TextField
                label="Description (optional)"
                value={sectionForm.description}
                onChange={e => onSectionFormChange('description', e.target.value)}
                variant="outlined" size="small" fullWidth
                placeholder="e.g. Covered outdoor patio, dog-friendly"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Sort Order"
                type="number"
                value={sectionForm.sort_order}
                onChange={e => onSectionFormChange('sort_order', Number(e.target.value))}
                variant="outlined" size="small" fullWidth
                inputProps={{ min: 0 }}
                helperText="Lower = shown first"
              />
            </Grid>
            <Grid item xs={6}>
              <Box display="flex" alignItems="center" height="100%">
                <FormControlLabel
                  control={
                    <Switch
                      checked={sectionForm.is_active}
                      onChange={e => onSectionFormChange('is_active', e.target.checked)}
                      color="primary"
                    />
                  }
                  label="Active"
                />
              </Box>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSectionModal}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveSection}
            disabled={!sectionForm.name || !sectionForm.total_capacity || isSavingSection}
          >
            {isSavingSection ? <CircularProgress size={18} /> : editingSection ? 'Save Changes' : 'Add Section'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Service Period Modal */}
      <Dialog open={spModalOpen} onClose={onCloseSpModal} maxWidth="sm" fullWidth>
        <DialogTitle>{editingSp ? 'Edit Service Period' : 'New Service Period'}</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <TextField
                label="Name (e.g. Dinner Service)"
                value={spForm.name}
                onChange={e => onSpFormChange('name', e.target.value)}
                variant="outlined" size="small" fullWidth
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Start Time"
                type="time"
                value={spForm.start_time}
                onChange={e => onSpFormChange('start_time', e.target.value)}
                variant="outlined" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="End Time"
                type="time"
                value={spForm.end_time}
                onChange={e => onSpFormChange('end_time', e.target.value)}
                variant="outlined" size="small" fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography variant="body2" style={{ fontWeight: 600, marginBottom: 4 }}>Days</Typography>
              <Box display="flex" flexWrap="wrap" gap={4}>
                {DAYS.map((day, i) => (
                  <FormControlLabel
                    key={day}
                    control={
                      <Checkbox
                        checked={spForm.days_of_week.includes(i)}
                        onChange={e => {
                          const next = e.target.checked
                            ? [...spForm.days_of_week, i].sort()
                            : spForm.days_of_week.filter(d => d !== i);
                          onSpFormChange('days_of_week', next);
                        }}
                        color="primary"
                        size="small"
                      />
                    }
                    label={day}
                    style={{ marginRight: 4 }}
                  />
                ))}
              </Box>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Covers (total capacity)"
                type="number"
                value={spForm.max_covers}
                onChange={e => onSpFormChange('max_covers', Number(e.target.value))}
                variant="outlined" size="small" fullWidth
                inputProps={{ min: 1 }}
                helperText="Total seats available per service"
              />
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Turn Time (minutes)"
                type="number"
                value={spForm.turn_time_mins}
                onChange={e => onSpFormChange('turn_time_mins', Number(e.target.value))}
                variant="outlined" size="small" fullWidth
                inputProps={{ min: 15, step: 15 }}
                helperText="How long each table is held"
              />
            </Grid>
            <Grid item xs={6}>
              <FormControl variant="outlined" size="small" fullWidth>
                <InputLabel>Slot Interval</InputLabel>
                <Select
                  value={spForm.slot_interval_mins}
                  onChange={e => onSpFormChange('slot_interval_mins', e.target.value)}
                  label="Slot Interval"
                >
                  <MenuItem value={15}>Every 15 min</MenuItem>
                  <MenuItem value={30}>Every 30 min</MenuItem>
                  <MenuItem value={60}>Every 60 min</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField
                label="Max Covers Per Slot (optional)"
                type="number"
                value={spForm.pacing_per_slot}
                onChange={e => onSpFormChange('pacing_per_slot', e.target.value)}
                variant="outlined" size="small" fullWidth
                inputProps={{ min: 1 }}
                helperText="Limit covers per time slot"
              />
            </Grid>
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Switch
                    checked={spForm.is_active}
                    onChange={e => onSpFormChange('is_active', e.target.checked)}
                    color="primary"
                  />
                }
                label="Active (accepts bookings)"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseSpModal}>Cancel</Button>
          <Button
            variant="contained"
            color="primary"
            onClick={onSaveServicePeriod}
            disabled={!spForm.name || spForm.days_of_week.length === 0 || isSavingSp}
          >
            {isSavingSp ? <CircularProgress size={18} /> : editingSp ? 'Save Changes' : 'Create Period'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Waitlist Modal */}
      <Dialog open={waitlistModalOpen} onClose={onCloseWaitlistModal} maxWidth="xs" fullWidth>
        <DialogTitle>Add to Waitlist</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} style={{ marginTop: 4 }}>
            <Grid item xs={12}>
              <FormControl variant="outlined" fullWidth size="small">
                <InputLabel>Guest</InputLabel>
                <Select
                  value={waitlistForm.guest_id}
                  onChange={e => onWaitlistFormChange('guest_id', e.target.value)}
                  label="Guest"
                >
                  {guests.map(g => (
                    <MenuItem key={g.id} value={g.id}>{g.first_name} {g.last_name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={6}>
              <TextField label="Party Size" type="number" value={waitlistForm.party_size}
                onChange={e => onWaitlistFormChange('party_size', Number(e.target.value))}
                variant="outlined" size="small" fullWidth inputProps={{ min: 1 }} />
            </Grid>
            <Grid item xs={6}>
              <TextField label="Preferred Time" type="time" value={waitlistForm.preferred_time}
                onChange={e => onWaitlistFormChange('preferred_time', e.target.value)}
                variant="outlined" size="small" fullWidth InputLabelProps={{ shrink: true }} />
            </Grid>
            <Grid item xs={12}>
              <TextField label="Est. Wait (mins)" type="number" value={waitlistForm.quoted_wait_mins}
                onChange={e => onWaitlistFormChange('quoted_wait_mins', Number(e.target.value))}
                variant="outlined" size="small" fullWidth />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={onCloseWaitlistModal}>Cancel</Button>
          <Button variant="contained" color="primary" onClick={onSaveWaitlist}
            disabled={!waitlistForm.guest_id}>
            Add to Waitlist
          </Button>
        </DialogActions>
      </Dialog>
    </PageWrap>
  );
};

export default ReservationsView;
